import { isInventoryPracticeActive } from '../../onboarding/practice/inventoryPractice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  secondaryText,
  smallTextSize,
  selectableName,
} from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { useInventoryViewStore as useInventoryStore } from '../../onboarding/practice/inventoryPractice';
import {
  CountReviewProduct,
  CountReviewAssignment,
  AvailableProduct,
  AvailableCustomIngredient,
  AvailableSubrecipe,
  formatNormalizedUnitPrice,
} from '../../../services/inventory/inventoryService';
import { useFuzzySearch } from '../../../hooks/useFuzzySearch';
import { baseProductKey } from '../../../utils/inventoryProductKey';
import { TabbedSections } from '../../ui/TabbedSections';
import { LabeledFilterButton } from '../../ui/LabeledFilterButton';
import { fmtQuantity } from './inventoryReviewShared';
import { relinkMatchProduct } from '../../onboarding/inventoryValues';

/**
 * The assignment being re-pointed. Kept minimal so the drawer serves both the count-review
 * tables (a `CountReviewProduct` row) and the config page (an `InventorySectionProduct`).
 */
export interface RelinkTarget {
  inventorySectionProductId: string | null;
  productName: string;
  sectionId: string | null;
  sectionName: string;
  inventoryCategoryName?: string | null;
  /** Frozen count key + category — the fallback identity when the assignment is gone. */
  productKey?: string;
  inventoryCategoryId?: string | null;
  /** Count context — only set when opened from a counted review row. */
  count?: number;
  countUnit?: string;
  /**
   * Drop custom ingredients from the SUGGESTED tab (the Custom ingredients tab still
   * lists them). Set from the count-review surfaces, where the whole point of the action
   * is clearing a "Not linked" flag: `countReviewBuilder` only counts a row as linked when
   * its source is `order` or `subrecipe`, so suggesting a custom ingredient there sends the
   * user in a circle. The config + leakage entry points leave it off and offer everything.
   */
  suggestLinkableOnly?: boolean;
  /**
   * The OTHER assignments of this same product, when the caller opened a row that stands for
   * several (the super-admin "Not linked (uncounted)" tab dedupes one row per product). The
   * chosen target is applied to each of them too — re-linking is per section (M40), so leaving
   * these behind would fix one shelf and leave the rest custom.
   */
  additionalAssignments?: { inventorySectionProductId: string; sectionId: string }[];
}

export function relinkTargetFromReviewRow(
  row: CountReviewProduct,
  /** Every assignment the row stands for (first one included) — only the deduped
   *  super-admin rows carry these; a normal counted row is a single assignment. */
  assignments?: CountReviewAssignment[]
): RelinkTarget {
  return {
    inventorySectionProductId: row.inventorySectionProductId,
    productName: row.productName,
    sectionId: row.sectionId,
    sectionName: row.sectionName,
    inventoryCategoryName: row.inventoryCategoryName,
    productKey: row.productKey,
    inventoryCategoryId: row.inventoryCategoryId,
    // Never-counted config rows have no count to show.
    count: row.neverCounted ? undefined : row.count,
    countUnit: row.countUnit,
    suggestLinkableOnly: true,
    additionalAssignments: (assignments ?? [])
      .filter((a) => a.inventorySectionProductId !== row.inventorySectionProductId)
      .map((a) => ({ inventorySectionProductId: a.inventorySectionProductId, sectionId: a.sectionId })),
  };
}

interface Props {
  /** The product whose link is being edited. Null → drawer renders nothing. */
  product: RelinkTarget | null;
  restaurantId: string;
  onClose: () => void;
}

/** A candidate the user can re-link to — an order-history product, a custom ingredient or a subrecipe. */
type Candidate =
  | { source: 'order'; item: AvailableProduct }
  | { source: 'custom'; item: AvailableCustomIngredient }
  | { source: 'subrecipe'; item: AvailableSubrecipe };

/**
 * Re-link drawer: slides in from the right. Re-points a "Not linked" inventory product
 * at a different order-history product or custom ingredient.
 */
export default function ReLinkInventoryProductModal({ product, restaurantId, onClose }: Props) {
  const { t } = useTranslation();
  const {
    availableProducts,
    availableCustomIngredients,
    availableSubrecipes,
    isLoadingAvailable,
    loadAvailableProducts,
    loadAvailableCustomIngredients,
    loadAvailableSubrecipes,
    relinkSectionProduct,
    relinkSectionProductByKey,
  } = useInventoryStore();

  const [tab, setTab] = useState<'suggested' | 'products' | 'custom' | 'subrecipe'>('suggested');
  const [search, setSearch] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const savingLock = useRef(false);

  const isOpen = product != null;

  // What this row points at TODAY. Variant rows (`…__v2`) are a second row of the same
  // product, so their link lives on the base key — that is what the pickers list.
  const currentKey = product?.productKey ? baseProductKey(product.productKey) : null;

  useEffect(() => {
    if (!isOpen) return;
    setTab('suggested');
    setSearch('');
    setSelectedPlatforms([]);
    setSelectedCategories([]);
    setSaving(false);
    savingLock.current = false;
    setErrorKey(null);
    loadAvailableProducts(restaurantId);
    loadAvailableCustomIngredients(restaurantId);
    loadAvailableSubrecipes(restaurantId);
  }, [isOpen, restaurantId, loadAvailableProducts, loadAvailableCustomIngredients, loadAvailableSubrecipes]);

  // Start on the current link so the user can SEE what they are changing. Confirm stays
  // disabled until they move off it — re-linking to the same target is a no-op that would
  // still reset the row's count unit + factor to the target's defaults.
  // Own effect (not the reset above): the current key changes per opened row, and folding
  // it into those deps would refetch all three pickers every time.
  useEffect(() => {
    setSelectedKey(currentKey);
  }, [currentKey]);

  // Lock background page scroll while the drawer is open (same idiom as the
  // cost-price detail modals). Cleanup restores it on close or unmount — a drawer
  // underneath re-applies its own lock when this one goes away.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Escape closes THIS drawer only. Opened from another slide-over (leakage), that one
  // stands down while this is up, so the key never collapses both layers at once.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const allCandidates = useMemo<Candidate[]>(() => [
    ...availableProducts.map((item) => ({ source: 'order' as const, item })),
    ...availableCustomIngredients.map((item) => ({ source: 'custom' as const, item })),
    ...availableSubrecipes.map((item) => ({ source: 'subrecipe' as const, item })),
  ], [availableProducts, availableCustomIngredients, availableSubrecipes]);

  const currentCandidate = useMemo(
    () => (currentKey ? allCandidates.find((c) => c.item.productKey === currentKey) ?? null : null),
    [allCandidates, currentKey]
  );

  // Filtered BEFORE the fuzzy pass, so dropping custom ingredients frees up slots in the
  // top-5 for order products instead of just shortening the list.
  const suggestionPool = useMemo(
    () => (product?.suggestLinkableOnly ? allCandidates.filter((c) => c.source !== 'custom') : allCandidates),
    [allCandidates, product?.suggestLinkableOnly]
  );

  const suggestions = useFuzzySearch({
    data: suggestionPool,
    keys: ['item.productName'],
    query: product?.productName ?? '',
    threshold: 0.6,
  }).slice(0, 5);

  // Filter options come from the full list, so a chosen supplier never hides the
  // categories you could still pick (same idiom as AssignProductsModal).
  const platformOptions = useMemo(
    () => Array.from(new Set(availableProducts.map((p) => p.platform).filter(Boolean))).sort(),
    [availableProducts]
  );

  const categoryOptions = useMemo(
    () => Array.from(new Set(availableProducts.map((p) => p.category).filter((c): c is string => !!c))).sort(),
    [availableProducts]
  );

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableProducts.filter((p) => {
      if (q && !p.productName.toLowerCase().includes(q)) return false;
      if (selectedPlatforms.length > 0 && !selectedPlatforms.includes(p.platform)) return false;
      if (selectedCategories.length > 0 && (!p.category || !selectedCategories.includes(p.category))) return false;
      return true;
    });
  }, [availableProducts, search, selectedPlatforms, selectedCategories]);

  const filteredCustom = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableCustomIngredients.filter((c) => !q || c.productName.toLowerCase().includes(q));
  }, [availableCustomIngredients, search]);

  const filteredSubrecipes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableSubrecipes.filter((s) => !q || s.productName.toLowerCase().includes(q));
  }, [availableSubrecipes, search]);

  const handleConfirm = async () => {
    if (savingLock.current || saving) return;
    if (!selectedKey || !product) return;
    if (!product.inventorySectionProductId && !(product.sectionId && product.productKey)) return;
    const custom = availableCustomIngredients.find((c) => c.productKey === selectedKey);
    const subrecipe = availableSubrecipes.find((s) => s.productKey === selectedKey);
    const payload = custom
      ? { productKey: selectedKey, source: 'custom' as const, customIngredientId: custom.customIngredientId }
      : subrecipe
        ? { productKey: selectedKey, source: 'subrecipe' as const, subrecipeId: subrecipe.subrecipeId }
        : { productKey: selectedKey, source: 'order' as const };
    savingLock.current = true;
    setSaving(true);
    setErrorKey(null);
    try {
      if (product.inventorySectionProductId) {
        await relinkSectionProduct(product.inventorySectionProductId, payload, restaurantId, product.sectionId);
        // Same product in other sections: one confirmation fixes every shelf. Sequential so a
        // failure surfaces on the first bad section rather than being lost in a race; each call
        // is its own server transaction, so earlier sections stay fixed.
        for (const extra of product.additionalAssignments ?? []) {
          await relinkSectionProduct(extra.inventorySectionProductId, payload, restaurantId, extra.sectionId);
        }
      } else {
        // No assignment behind this counted row — fix it by its frozen key instead.
        await relinkSectionProductByKey(
          product.sectionId as string,
          { ...payload, oldProductKey: product.productKey as string, inventoryCategoryId: product.inventoryCategoryId ?? null },
          restaurantId
        );
      }
      onClose();
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      setErrorKey(code === 'already_in_section' ? 'inventory.relinkAlreadyInSection' : 'inventory.relinkError');
      savingLock.current = false;
      setSaving(false);
    }
  };

  const handleConfirmRef = useRef(handleConfirm);
  handleConfirmRef.current = handleConfirm;

  useEffect(() => {
    if (!isOpen) return;
    if (!isInventoryPracticeActive()) return;
    const onTab = (e: Event) => {
      const next = (e as CustomEvent<{ tab?: 'suggested' | 'products' | 'custom' | 'subrecipe' }>).detail?.tab;
      if (next === 'suggested' || next === 'products' || next === 'custom' || next === 'subrecipe') setTab(next);
    };
    const onSearch = (e: Event) => {
      const query = (e as CustomEvent<{ query?: string }>).detail?.query;
      if (typeof query !== 'string') return;
      setTab('products');
      setSearch(query);
    };
    const onSelect = (e: Event) => {
      const name = (e as CustomEvent<{ name?: string }>).detail?.name;
      if (!name) return;
      const q = name.toLowerCase();
      const match =
        availableProducts.find(p => p.productName.toLowerCase() === q) ??
        availableProducts.find(p => p.productName.toLowerCase().includes(q));
      if (!match) return;
      setTab('products');
      setSelectedKey(match.productKey);
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>('[data-onboarding~="inventory-relink-pick"]')?.scrollIntoView({ block: 'nearest' });
      });
    };
    const onConfirm = () => {
      void handleConfirmRef.current();
    };
    window.addEventListener('onboarding:inventory-relink-tab', onTab);
    window.addEventListener('onboarding:inventory-relink-search', onSearch);
    window.addEventListener('onboarding:inventory-relink-select', onSelect);
    window.addEventListener('onboarding:inventory-relink-confirm', onConfirm);
    return () => {
      window.removeEventListener('onboarding:inventory-relink-tab', onTab);
      window.removeEventListener('onboarding:inventory-relink-search', onSearch);
      window.removeEventListener('onboarding:inventory-relink-select', onSelect);
      window.removeEventListener('onboarding:inventory-relink-confirm', onConfirm);
    };
  }, [isOpen, availableProducts]);

  const walkthroughKey = relinkMatchProduct()?.productKey;

  const renderRow = (candidate: Candidate, showBadge: boolean) => {
    const { item } = candidate;
    const isSelected = selectedKey === item.productKey;
    const isCurrent = item.productKey === currentKey;
    const price = formatNormalizedUnitPrice(item);
    // Only order products and custom ingredients carry a supplier; a prep is made in house.
    const platform = candidate.source === 'subrecipe' ? null : candidate.item.platform;
    return (
      <button
        type="button"
        key={item.productKey}
        onClick={() => setSelectedKey(selectedKey === item.productKey ? null : item.productKey)}
        data-onboarding={item.productKey === walkthroughKey ? 'inventory-relink-pick' : undefined}
        className={cn(
          'w-full flex items-center justify-between gap-3 pl-3 pr-4 py-3 rounded-lg border cursor-pointer transition-colors text-left',
          isSelected
            ? 'bg-[#0E89651A] dark:bg-green-900/20 border-[#0E8965] dark:border-green-600'
            : 'bg-[#F9F9FA] dark:bg-zinc-900 border-[#E8E8E9] dark:border-[#1E3A4E] dark:bg-[#0E2B42]'
        )}
      >
        <div className="flex-1 min-w-0">
          {/* Wraps: the distinguishing part of a product name (vintage, size) sits at the end,
              so the full name must stay readable even when it needs a second/third line. */}
          <div className="text-[15px] font-semibold text-[#101828] dark:text-zinc-50 flex flex-wrap items-center gap-2">
            <span className={cn('text-[14px] sm:text-[16px] leading-5 text-[#101828] dark:text-[#F8FAFC]', selectableName)}>{item.productName}</span>
            {showBadge && candidate.source === 'custom' && (
              <span className="flex-none text-[10px] leading-5 uppercase tracking-wide px-1.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {t('inventory.customBadge')}
              </span>
            )}
            {showBadge && candidate.source === 'subrecipe' && (
              <span className="flex-none text-[10px] leading-5 uppercase tracking-wide px-1.5 rounded bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                {t('inventory.subrecipeBadge')}
              </span>
            )}
            {isCurrent && (
              <span className="flex-none text-[10px] leading-5 uppercase tracking-wide px-1.5 rounded bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-200">
                {t('inventory.relinkCurrentBadge')}
              </span>
            )}
          </div>
          <div className="mt-2.5 text-[14px] sm:text-[16px] leading-5 sm:leading-6 text-[#667085] dark:text-[#CBD5E1]">
            {platform && platform.toLowerCase() !== 'unsupported' && <span>{platform} • </span>}
            {price && <span>{currencyFormatter.format(price.value)} / {price.unit}</span>}
          </div>
        </div>
        {/* Checkbox indicator */}
        <div className={cn(
          'flex-shrink-0 w-5 h-5 max-h-5 max-w-5 border flex items-center justify-center transition-colors',
          isSelected
            ? 'border-[#0E8965] bg-[#0E8965] rounded-full'
            : 'border-[#D0D5DD] dark:border-zinc-600 bg-white dark:bg-white rounded-full'
        )}>
          {isSelected && (
            <div className="w-4 h-4 rounded-full border-2 border-[#0E8965] dark:border-[#0E8965] bg-white dark:bg-[#0E2B42]" />
          )}
        </div>
      </button>
    );
  };

  return (
    <>
      {/* Backdrop — always rendered but fades in/out */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 dark:bg-black/60 transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer — slides in from the right */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-[584px]',
          'bg-white dark:bg-[#0A2133] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        data-onboarding={isOpen ? 'inventory-relink' : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar with back arrow */}
        <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] transition-colors"
            aria-label={t('inventory.cancel') as string}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dark:text-[#CBD5E1] text-[#000]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0003 15.8332L4.16699 9.99984L10.0003 4.1665" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 px-4 sm:px-5 py-3 sm:py-5 flex flex-col overflow-y-auto">
          {product && (
            <>
              {product.inventoryCategoryName && (
                <span className="mb-4 inline-flex self-start items-center px-2.5 rounded-full text-[14px] leading-[30px] font-semibold bg-[#2E90FA] dark:bg-backgroundBlue-dark text-white dark:text-white">
                  {product.inventoryCategoryName}
                </span>
              )}

              <h2 className="mb-2 sm:mb-3 text-[24px] sm:text-[28px] leading-[36px] sm:leading-[39px] font-semibold sm:font-bold leading-tight text-[#161924] dark:text-zinc-50">{t('inventory.relinkTitle')}</h2>
              <p className={cn(smallTextSize, secondaryText, "text-[14px] sm:text-[16px] leading-[18px] sm:leading-[21px] font-normal")}>
                <span>{t('inventory.relinkDescription')}</span>
              </p>

              <div className={cn('mt-5 sm:mt-4')}>
                {/* Current product context */}
                <p className="mb-2.5 sm:mb-4 text-[16px] leading-[30px] font-semibold text-[#101828] dark:text-zinc-50">
                  {product.productName}
                </p>

                {/* What it points at today — otherwise the drawer gives no clue what is
                    being changed, and "Not linked" rows look identical to linked ones. */}
                <div className="mb-5 sm:mb-6 rounded-xl border border-[#E5E7EB] dark:border-[#0E2B42] dark:bg-[#0E2B42] px-4 sm:px-5 py-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
                  <span className={cn(smallTextSize, secondaryText, 'block')}>{t('inventory.relinkCurrentLabel')}</span>
                  {currentCandidate ? (
                    <span className="mt-1 flex items-center gap-2 flex-wrap text-[16px] leading-6 font-semibold text-[#101828] dark:text-zinc-50">
                      <span className="min-w-0 break-words">{currentCandidate.item.productName}</span>
                      {currentCandidate.source === 'custom' && (
                        <span className="flex-none text-[10px] leading-5 uppercase tracking-wide px-1.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          {t('inventory.customBadge')}
                        </span>
                      )}
                      {currentCandidate.source === 'subrecipe' && (
                        <span className="flex-none text-[10px] leading-5 uppercase tracking-wide px-1.5 rounded bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                          {t('inventory.subrecipeBadge')}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className={cn('mt-1 block text-[16px] leading-6 font-semibold', secondaryText)}>
                      {isLoadingAvailable ? t('inventory.loading') : t('inventory.relinkCurrentNone')}
                    </span>
                  )}
                </div>

                <div className={cn('mb-5 sm:mb-6 grid gap-2.5 sm:gap-4', product.count != null ? 'grid-cols-2' : 'grid-cols-1')}>
                  <div className="rounded-xl border border-[#E5E7EB] dark:border-[#0E2B42] dark:bg-[#0E2B42] px-4 sm:px-5 py-3 sm:py-[13px] flex flex-col gap-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
                    <span className="text-[18px] leading-[24px] font-semibold text-[#101828] dark:text-[#F8FAFC]">
                      {t('inventory.section')}
                    </span>
                    <span className="text-[24px] leading-[38px] font-semibold text-[#0C111D] dark:text-zinc-50">
                      {product.sectionName}
                    </span>
                    {/* A deduped row re-links every section it stands for — say so before confirming. */}
                    {(product.additionalAssignments?.length ?? 0) > 0 && (
                      <span className={cn(secondaryText, smallTextSize)}>
                        {t('inventory.relinkAlsoSections', { count: product.additionalAssignments!.length })}
                      </span>
                    )}
                  </div>

                  {product.count != null && (
                    <div className="rounded-xl border border-[#E5E7EB] dark:border-[#0E2B42] dark:bg-[#0E2B42] px-4 sm:px-5 py-3 sm:py-[13px] flex flex-col gap-3 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
                      <span className="text-[18px] leading-[24px] font-semibold text-[#101828] dark:text-[#F8FAFC]">
                        {t('inventory.counted')}
                      </span>
                      <span className="text-[24px] leading-[38px] font-semibold text-[#0C111D] dark:text-zinc-50">
                        {fmtQuantity(product.count)} {product.countUnit}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {errorKey && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">{t(errorKey)}</p>
                </div>
              )}

              <TabbedSections
                options={[
                  { value: 'suggested', label: t('inventory.relinkTabSuggested') },
                  { value: 'products', label: t('inventory.tabOrderProducts') },
                  { value: 'custom', label: t('inventory.tabCustomIngredients') },
                  { value: 'subrecipe', label: t('inventory.tabSubrecipes') },
                ]}
                value={tab}
                onChange={(v) => setTab(v as 'suggested' | 'products' | 'custom' | 'subrecipe')}
                isTimeOptionSelection={false}
                maxTabsBeforeDropdown={4}
                fullWidth
                className='mb-4 sm:mb-5'
                onboardingId="inventory-relink-tab"
              />

              {/* Supplier + product-category filters — order products only (custom
                  ingredients carry neither). */}
              {tab === 'products' && (
                <div className="mb-4 sm:mb-5 flex gap-2 flex-wrap">
                  <LabeledFilterButton
                    label={t('inventory.filterPlatform')}
                    selected={selectedPlatforms}
                    onChange={setSelectedPlatforms}
                    options={platformOptions}
                    valueType="platform"
                  />
                  <LabeledFilterButton
                    label={t('inventory.filterCategory')}
                    selected={selectedCategories}
                    onChange={setSelectedCategories}
                    options={categoryOptions}
                    valueType="category"
                  />
                </div>
              )}

              {/* Search */}
              {tab !== 'suggested' && (
                <div className="mb-5 relative w-full">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085] dark:text-[#CBD5E1]" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('inventory.searchProducts') as string}
                    data-onboarding="inventory-relink-search"
                    className={cn(
                      'w-full pl-10 pr-4 h-11 rounded-lg border outline-none text-[16px] leading-6 font-normal',
                      borderPrimary,
                      'bg-white dark:bg-[#14344C] dark:border-[#2D4F68]',
                      'text-zinc-900 dark:text-zinc-50',
                    )}
                  />
                </div>
              )}

              {/* Results list */}
              <div className="p-3 sm:p-4 rounded-lg border border-[#E8E8E9] dark:border-[#2D4F68] shadow-[0px_1px_1.5px_0.1px_#16192408] dark:shadow-[0px_1px_1.5px_0.1px_#00000040] flex flex-col">
                {isLoadingAvailable ? (
                  <p className={cn(smallTextSize, secondaryText, 'text-center py-6')}>{t('inventory.loading')}</p>
                ) : tab === 'suggested' ? (
                  suggestions.length === 0 ? (
                    <p className={cn(smallTextSize, secondaryText, 'text-center py-6')}>{t('inventory.relinkNoSuggestions')}</p>
                  ) : (
                    <>
                      <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-zinc-50 flex items-center gap-1 mb-3 flex-shrink-0">
                        {t('inventory.relinkSuggestions')}
                        <span>
                          ({suggestions.length})
                        </span>
                      </p>
                      <div className="max-h-[55vh] overflow-y-auto space-y-3 custom-scrollbar">
                        {suggestions.map((c) => renderRow(c, true))}
                      </div>
                    </>
                  )
                ) : tab === 'subrecipe' ? (
                  filteredSubrecipes.length === 0 ? (
                    <p className={cn(smallTextSize, secondaryText, 'text-center py-6')}>
                      {availableSubrecipes.length === 0 ? t('inventory.noSubrecipes') : '—'}
                    </p>
                  ) : (
                    <>
                      <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-zinc-50 flex items-center gap-1 mb-3 flex-shrink-0">
                        {t('inventory.tabSubrecipes')}
                        <span>
                          ({availableSubrecipes.length})
                        </span>
                      </p>
                      <div className="max-h-[55vh] overflow-y-auto space-y-3 custom-scrollbar">
                        {filteredSubrecipes.map((s) => renderRow({ source: 'subrecipe', item: s }, false))}
                      </div>
                    </>
                  )
                ) : tab === 'custom' ? (
                  filteredCustom.length === 0 ? (
                    <p className={cn(smallTextSize, secondaryText, 'text-center py-6')}>
                      {availableCustomIngredients.length === 0 ? t('inventory.noCustomIngredients') : '—'}
                    </p>
                  ) : (
                    <>
                      <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-zinc-50 flex items-center gap-1 mb-3 flex-shrink-0">
                        {t('inventory.tabCustomIngredients')}
                        <span>
                          ({availableCustomIngredients.length})
                        </span>
                      </p>
                      <div className="max-h-[55vh] overflow-y-auto space-y-3 custom-scrollbar">
                        {filteredCustom.map((c) => renderRow({ source: 'custom', item: c }, false))}
                      </div>
                    </>
                  )
                ) : filteredProducts.length === 0 ? (
                  <p className={cn(smallTextSize, secondaryText, 'text-center py-6')}>
                    {availableProducts.length === 0 ? t('inventory.noAvailableProducts') : '—'}
                  </p>
                ) : (
                  <>
                    <p className="text-[16px] leading-5 font-semibold text-[#0C111D] dark:text-zinc-50 flex items-center gap-1 mb-3 flex-shrink-0">
                      {t('inventory.tabOrderProducts')}
                      <span>
                        ({filteredProducts.length})
                      </span>
                    </p>
                    <div className="max-h-[55vh] overflow-y-auto space-y-3 custom-scrollbar">
                      {filteredProducts.map((p) => renderRow({ source: 'order', item: p }, false))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer — sticky at bottom */}
        <div className="flex gap-3 sm:gap-6 px-4 sm:px-5 pb-4 sm:pb-5 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
          >
            {t('inventory.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !selectedKey || selectedKey === currentKey}
            data-onboarding="inventory-relink-confirm"
            data-saving={saving ? '' : undefined}
            className="flex items-center gap-1.5 flex-1 justify-center px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_305_18161" maskUnits="userSpaceOnUse" x="1" y="1" width="18" height="18">
                <path d="M18 18V2H2V18H18Z" fill="white" stroke="white" strokeWidth="2" />
              </mask>
              <g mask="url(#mask0_305_18161)">
                <path d="M10.4912 8.27068C10.7424 8.41375 10.9779 8.5934 11.1923 8.80773C12.4798 10.0953 12.5151 12.1475 10.8953 13.7674L8.70148 15.9612C7.08164 17.581 5.02936 17.5457 3.74182 16.2582C2.45429 14.9706 2.41901 12.9184 4.03882 11.2985L5.63599 9.69481" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.50929 11.7292C9.2581 11.5862 9.02257 11.4066 8.80821 11.1922C7.52068 9.90463 7.48543 7.85239 9.10524 6.23258L11.299 4.03879C12.9189 2.41895 14.9711 2.45423 16.2587 3.74176C17.5462 5.02933 17.5815 7.08157 15.9617 8.70139L14.3645 10.3051" stroke="white" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </svg>

            {saving ? t('inventory.loading') : t('inventory.relinkConfirm')}
          </button>
        </div>

      </div >
    </>
  );
}
